import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';

import { environment } from '@env/environment';
import { Credentials } from '@core/entities';
import { isMobileView, SetHeadlineOption } from '@core/utils';
import { CredentialsService } from '@auth';

export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
  isMobile?: boolean;
}

/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly _credentialsService = inject(CredentialsService);
  private readonly _http = inject(HttpClient);

  private readonly _apiUrl = environment.apiUrl;

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user credentials.
   */
  login(context: LoginContext): Observable<Credentials> {
    const data: LoginContext = {
      username: context.username,
      password: context.password,
      remember: context.remember ?? true,
      isMobile: isMobileView(),
    };

    return this._http.post(this._apiUrl + '/login-keycloak', data).pipe(
      map((result: any) => {
        const transformedCredentials = plainToClass(Credentials, result);

        if (transformedCredentials.deletedUser) {
          throw { error: { message: 'Your account has been deleted. Please contact support.' } };
        }

        this._credentialsService.setCredentials(transformedCredentials, context.remember);
        if (result.customerTheme && result.customerTheme.length) {
          if (result.customerTheme[0].wording !== null && result.customerTheme[0].wording !== undefined) {
            SetHeadlineOption(result.customerTheme[0].wording);
          }
        }
        return transformedCredentials;
      }),
    );
  }

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout(): Observable<any> {
    const userId = this._credentialsService.credentials?.userId;
    if (userId) {
      return this._http.put(this._apiUrl + `/setLoginStatus/${userId}`, null);
    }

    return of(true);
  }

  submitPasswordReset(username: string): Observable<any> {
    return this._http.post(this._apiUrl + '/forgot-password', { username });
  }
}
